import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 3 (Asignado) de una OT de Remplazo Equipos Vendidos Activos (tipo_ot=16):
// mismo patrón de panel que AsignarReemplazoOT.jsx (Reemplazo original), con un campo
// de precio de venta agregado (prefilled desde /precio-sugerido) — no aplica a Chip
// (exclusivo de POC), que nunca se vende y queda simplemente 'Activo'. El equipo nuevo
// SÍ genera una fila de historial acá (a diferencia del Reemplazo original) para no
// perder el precio acordado hasta Finalizar.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};
const inputCompactoSx = {
  fontFamily: "inherit",
  fontSize: 11.5,
  padding: "4px 6px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 4,
  outline: "none",
  bgcolor: "transparent",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { color: "text.disabled" },
};

const TIPO_LABEL = { RADIO: "Radio", BATERIA: "Batería", VARIOS: "Equipo", CHIP: "Chip" };
const PAGE_SIZE = 10;
const ESTADOS_FINALES = ["Inactivo", "Mantenimiento", "Baja", "Robado"];
const ESTADO_DEFAULT = { RADIO: "Mantenimiento", BATERIA: "Mantenimiento", VARIOS: "Inactivo", CHIP: "Inactivo" };

const endpointDeFamilia = (familia) => {
  if (familia === "RADIO") return "/api/equipos-radio";
  if (familia === "BATERIA") return "/api/equipos-bateria";
  if (familia === "CHIP") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

const identificadorDe = (r) => r.serie ?? r.numero ?? r.num_sim;
const keyDe = (row) => `${row.familia}-${row.identificador}`;

const TIPOS_TKR = ["RADIO", "BATERIA"];
const TIPOS_POC = ["VARIOS", "CHIP"];
const TIPOS_OTROS = ["VARIOS"];

const AsignarRemplazoVendidosActivosOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, esPOC, estadoVendido = "Vendido Activo", onEquiposActualizados }) => {
  const { user } = useAuth();
  const [retirados, setRetirados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(null);
  const [quitando, setQuitando] = useState(null);

  const [selected, setSelected] = useState(null); // fila de `retirados` con panel abierto
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingItems, setLoadingItems] = useState(false);
  const [extra, setExtra] = useState({ grupo: "", placa: "", id_ref: "" });
  const [clasificacion, setClasificacion] = useState({ estadoFinal: "", observacion: "" });
  const [precioVenta, setPrecioVenta] = useState(0);
  const [asignando, setAsignando] = useState(null);
  const panelRef = React.useRef(null);

  const tiposAgregar = esTKR ? TIPOS_TKR : esPOC ? TIPOS_POC : TIPOS_OTROS;
  const [agregarAbierto, setAgregarAbierto] = useState(false);
  const [agregarTipo, setAgregarTipo] = useState(tiposAgregar[0]);
  const [agregarItems, setAgregarItems] = useState([]);
  const [agregarTotal, setAgregarTotal] = useState(0);
  const [agregarPage, setAgregarPage] = useState(1);
  const [agregarBuscar, setAgregarBuscar] = useState("");
  const [agregarBuscarAplicado, setAgregarBuscarAplicado] = useState("");
  const [agregarLoading, setAgregarLoading] = useState(false);
  const [agregando, setAgregando] = useState(null);
  const agregarTotalPages = Math.max(1, Math.ceil(agregarTotal / PAGE_SIZE));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(`/api/ot-remplazo-vendidos-activos/retirados/${cod_ot}`)
      .then((res) => setRetirados(res.data || []))
      .catch(() => setError("No se pudo cargar el estado del remplazo."))
      .finally(() => setLoading(false));
  }, [cod_ot]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarDisponibles = (familia, nextPage = 1, buscarTexto = "") => {
    setLoadingItems(true);
    axios.get(endpointDeFamilia(familia), {
      params: { estado: "Inactivo", ciudad: user?.sucursal, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data || {};
        const rows = data.items || data.rows || [];
        setItems(rows.map((r) => ({ key: r.ID ?? r.ID_A ?? identificadorDe(r), identificador: identificadorDe(r), modelo: r.modelo, ciudad: r.ciudad })));
        setTotal(Number(data.total || 0));
        setPage(Number(data.page || nextPage));
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoadingItems(false));
  };

  const cargarPrecioSugerido = async (familia) => {
    if (familia === "CHIP") return 0;
    try {
      const { data } = await axios.get("/api/ot-remplazo-vendidos-activos/precio-sugerido", {
        params: { cod_servicio_cliente, familia },
      });
      return Number(data?.precio || 0);
    } catch {
      return 0;
    }
  };

  const abrirBuscador = async (retirado) => {
    setAgregarAbierto(false);
    setSelected(retirado);
    setBuscar("");
    setBuscarAplicado("");
    setExtra({ grupo: retirado.grupo || "", placa: retirado.placa || "", id_ref: retirado.id_ref || "" });
    setClasificacion({ estadoFinal: ESTADO_DEFAULT[retirado.familia] || "Inactivo", observacion: "" });
    setPrecioVenta(await cargarPrecioSugerido(retirado.familia));
    cargarDisponibles(retirado.familia, 1, "");
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const cargarDisponiblesAgregar = (familia, nextPage = 1, buscarTexto = "") => {
    setAgregarLoading(true);
    axios.get(endpointDeFamilia(familia), {
      params: { estado: estadoVendido, cod_servicio: cod_servicio_cliente, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data || {};
        const rows = data.items || data.rows || [];
        setAgregarItems(rows.map((r) => ({ key: r.ID ?? r.ID_A ?? identificadorDe(r), identificador: identificadorDe(r), modelo: r.modelo })));
        setAgregarTotal(Number(data.total || 0));
        setAgregarPage(Number(data.page || nextPage));
      })
      .catch(() => { setAgregarItems([]); setAgregarTotal(0); })
      .finally(() => setAgregarLoading(false));
  };

  const abrirAgregar = () => {
    setSelected(null);
    setAgregarAbierto(true);
    setAgregarBuscar("");
    setAgregarBuscarAplicado("");
    cargarDisponiblesAgregar(agregarTipo, 1, "");
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const handleBuscarAgregar = () => {
    setAgregarBuscarAplicado(agregarBuscar.trim());
    cargarDisponiblesAgregar(agregarTipo, 1, agregarBuscar.trim());
  };

  const handleAgregar = async (row) => {
    setAgregando(row.key);
    setError("");
    try {
      await axios.post("/api/ot-remplazo-vendidos-activos/agregar-retirado", {
        cod_ot,
        cod_cliente,
        familia: agregarTipo,
        identificador: row.identificador,
      });
      cargar();
      cargarDisponiblesAgregar(agregarTipo, agregarPage, agregarBuscarAplicado);
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo agregar "${row.identificador}".`);
    } finally {
      setAgregando(null);
    }
  };

  const handleBuscar = () => {
    if (!selected) return;
    setBuscarAplicado(buscar.trim());
    cargarDisponibles(selected.familia, 1, buscar.trim());
  };

  const handleAsignar = async (row) => {
    if (!selected) return;
    const mensajePrecio = selected.familia === "CHIP" ? "" : ` con precio de venta ${Number(precioVenta) || 0}`;
    if (!window.confirm(`¿Asignar "${row.identificador}" como reemplazo de "${selected.identificador}"${mensajePrecio}?`)) return;
    setAsignando(row.key);
    setError("");
    try {
      await axios.post("/api/ot-remplazo-vendidos-activos/asignar", {
        cod_ot,
        familia: selected.familia,
        identificador: row.identificador,
        cod_cliente,
        cod_servicio_cliente,
        grupo: extra.grupo || "",
        placa: extra.placa || "",
        id_ref: extra.id_ref || "",
        reemplazaIdentificador: selected.identificador,
        estadoFinalRetirado: clasificacion.estadoFinal,
        observacionRetirado: clasificacion.observacion || "",
        precioVenta: selected.familia === "CHIP" ? 0 : Number(precioVenta) || 0,
      });
      setSelected(null);
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo asignar "${row.identificador}".`);
    } finally {
      setAsignando(null);
    }
  };

  const handleDeshacer = async (retirado) => {
    const pareja = retirado.pareja;
    if (!pareja) return;
    if (!window.confirm(`¿Deshacer el remplazo? "${pareja.identificador}" vuelve a Inactivo.`)) return;
    setDeshaciendo(keyDe(retirado));
    setError("");
    try {
      await axios.post("/api/ot-remplazo-vendidos-activos/deshacer", { cod_ot, familia: retirado.familia, identificador: pareja.identificador });
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo deshacer el remplazo.");
    } finally {
      setDeshaciendo(null);
    }
  };

  const handleQuitar = async (retirado) => {
    if (!window.confirm(`¿Quitar "${retirado.identificador}" de la lista de equipos a reemplazar? Vuelve a ${estadoVendido} con el cliente.`)) return;
    setQuitando(keyDe(retirado));
    setError("");
    try {
      await axios.post("/api/ot-remplazo-vendidos-activos/quitar-retirado", { cod_ot, cod_cliente, cod_servicio_cliente, familia: retirado.familia, identificador: retirado.identificador });
      if (selected && keyDe(selected) === keyDe(retirado)) setSelected(null);
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo quitar "${retirado.identificador}".`);
    } finally {
      setQuitando(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>{error}</MuiAlert>}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Equipos a Reemplazar ({estadoVendido})
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          onClick={abrirAgregar}
          sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
        >
          Agregar equipo a reemplazar
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 0.5 }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Tipo</TableCell>
              <TableCell sx={headCellSx}>Serie</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Estado Final</TableCell>
              <TableCell sx={headCellSx}>Observación</TableCell>
              <TableCell sx={{ ...headCellSx, width: 240 }}>Reemplazo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!retirados.length ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay equipos marcados para reemplazo en esta OT.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              retirados.map((row) => {
                const pareja = row.pareja;
                const rowKey = keyDe(row);
                const seleccionada = selected && keyDe(selected) === rowKey;
                return (
                  <TableRow key={rowKey} hover selected={seleccionada}>
                    <TableCell>{TIPO_LABEL[row.familia]}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.familia.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                    <TableCell>{pareja ? row.estadoFinal : <Chip size="small" label="Pendiente" sx={{ height: 18, fontSize: 10 }} />}</TableCell>
                    <TableCell>{pareja ? (row.observacion || "-") : "-"}</TableCell>
                    <TableCell>
                      {pareja ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${pareja.identificador} (${pareja.modelo})`}
                            onClick={() => setEquipoDialog({ tipo: row.familia.toLowerCase(), identificador: pareja.identificador })}
                            sx={{ height: 20, fontSize: 10.5, cursor: "pointer" }}
                          />
                          {!esTKR && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={deshaciendo === rowKey}
                              onClick={() => handleDeshacer(row)}
                              sx={{ minHeight: 20, height: 20, fontSize: 10 }}
                            >
                              {deshaciendo === rowKey ? <CircularProgress size={12} /> : "Deshacer"}
                            </Button>
                          )}
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => abrirBuscador(row)}
                            sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                          >
                            Buscar Reemplazo
                          </Button>
                          {!esTKR && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={quitando === rowKey}
                              onClick={() => handleQuitar(row)}
                              sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                            >
                              {quitando === rowKey ? <CircularProgress size={12} /> : "Quitar"}
                            </Button>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selected && (
        <Paper ref={panelRef} variant="outlined" sx={{ mb: 2, borderColor: "primary.main" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Buscando reemplazo para {selected.identificador} ({selected.modelo})
            </Typography>
            <IconButton size="small" onClick={() => setSelected(null)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              ¿Qué pasa con {selected.identificador}?
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <Select
                size="small"
                value={clasificacion.estadoFinal}
                onChange={(e) => setClasificacion((p) => ({ ...p, estadoFinal: e.target.value }))}
                sx={{ width: 150, fontSize: 11.5, height: 30 }}
              >
                {ESTADOS_FINALES.map((op) => (
                  <MenuItem key={op} value={op} sx={{ fontSize: 11 }}>{op}</MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                label="Observación"
                value={clasificacion.observacion}
                onChange={(e) => setClasificacion((p) => ({ ...p, observacion: e.target.value }))}
                fullWidth
                sx={controlSx}
              />
            </Stack>

            {selected.familia === "RADIO" && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <TextField size="small" label="Grupo" value={extra.grupo} onChange={(e) => setExtra((p) => ({ ...p, grupo: e.target.value }))} sx={{ width: 110, ...controlSx }} />
                <TextField size="small" label="Placa" value={extra.placa} onChange={(e) => setExtra((p) => ({ ...p, placa: e.target.value }))} sx={{ width: 110, ...controlSx }} />
                <TextField size="small" label="ID Cliente" value={extra.id_ref} onChange={(e) => setExtra((p) => ({ ...p, id_ref: e.target.value }))} sx={{ width: 130, ...controlSx }} />
              </Stack>
            )}

            {selected.familia !== "CHIP" && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Precio de venta del equipo de reemplazo:
                </Typography>
                <Box
                  component="input"
                  type="number"
                  value={precioVenta}
                  onChange={(e) => setPrecioVenta(e.target.value)}
                  sx={{ ...inputCompactoSx, width: 100 }}
                />
              </Stack>
            )}
            {selected.familia === "CHIP" && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, fontStyle: "italic" }}>
                El chip de reemplazo no lleva precio — queda como "Activo".
              </Typography>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
                sx={{ minWidth: 160, ...controlSx }}
              />
              <IconButton size="small" onClick={handleBuscar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
              <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 280, borderRadius: 0.5 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={headCellSx}>Ciudad</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 90 }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingItems ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 1.5 }}><CircularProgress size={16} /></TableCell></TableRow>
                  ) : !items.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          No hay {TIPO_LABEL[selected.familia].toLowerCase()}s Inactivo(a)s en esta sucursal.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.key} hover>
                        <TableCell>{row.identificador}</TableCell>
                        <TableCell>{row.modelo}</TableCell>
                        <TableCell>{row.ciudad}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={asignando === row.key}
                            onClick={() => handleAsignar(row)}
                            sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                          >
                            {asignando === row.key ? <CircularProgress size={14} color="inherit" /> : "Asignar"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Página {total ? page : 0} / {total ? totalPages : 0}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingItems || page <= 1}
                onClick={() => cargarDisponibles(selected.familia, page - 1, buscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Anterior
              </Button>
              <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingItems || page >= totalPages}
                onClick={() => cargarDisponibles(selected.familia, page + 1, buscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Siguiente
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {agregarAbierto && (
        <Paper ref={panelRef} variant="outlined" sx={{ mb: 2, borderColor: "primary.main" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Agregar equipo a reemplazar
            </Typography>
            <IconButton size="small" onClick={() => setAgregarAbierto(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          <Box sx={{ px: 1.5, pb: 1.5 }}>
            {tiposAgregar.length > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  color="primary"
                  value={agregarTipo}
                  onChange={(e, nuevo) => {
                    if (!nuevo) return;
                    setAgregarTipo(nuevo);
                    setAgregarBuscar("");
                    setAgregarBuscarAplicado("");
                    cargarDisponiblesAgregar(nuevo, 1, "");
                  }}
                  sx={{ height: 30 }}
                >
                  {tiposAgregar.map((t) => (
                    <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                      {TIPO_LABEL[t]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={agregarBuscar}
                onChange={(e) => setAgregarBuscar(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleBuscarAgregar(); }}
                sx={{ minWidth: 160, ...controlSx }}
              />
              <IconButton size="small" onClick={handleBuscarAgregar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
              <Chip size="small" label={`Total: ${agregarTotal}`} sx={{ height: 20, fontSize: 11 }} />
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 280, borderRadius: 0.5 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 90 }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agregarLoading ? (
                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 1.5 }}><CircularProgress size={16} /></TableCell></TableRow>
                  ) : !agregarItems.length ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          Este cliente no tiene {(TIPO_LABEL[agregarTipo] || "").toLowerCase()}s en {estadoVendido}.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    agregarItems.map((row) => (
                      <TableRow key={row.key} hover>
                        <TableCell>{row.identificador}</TableCell>
                        <TableCell>{row.modelo}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={agregando === row.key}
                            onClick={() => handleAgregar(row)}
                            sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                          >
                            {agregando === row.key ? <CircularProgress size={14} color="inherit" /> : "Agregar"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Página {agregarTotal ? agregarPage : 0} / {agregarTotal ? agregarTotalPages : 0}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={agregarLoading || agregarPage <= 1}
                onClick={() => cargarDisponiblesAgregar(agregarTipo, agregarPage - 1, agregarBuscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Anterior
              </Button>
              <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={agregarLoading || agregarPage >= agregarTotalPages}
                onClick={() => cargarDisponiblesAgregar(agregarTipo, agregarPage + 1, agregarBuscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Siguiente
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default AsignarRemplazoVendidosActivosOT;
